// File: src/pages/DesktopLanding.jsx
import React, { useState } from 'react';
import PortalLoginModal from '../components/host/modals/PortalLoginModal';

import { FaInstagram, FaLinkedin } from 'react-icons/fa';
import { IoChevronDown } from 'react-icons/io5';
import {
  LuCalendarCheck,
  LuUserRound,
  LuHandshake,
  LuMessagesSquare,
  LuLogIn,
} from 'react-icons/lu';

import { ReactComponent as TribeLogo } from './../assets/TribeLogo.svg';
import JoinTribeQR from '../assets/JoinTribeQR.svg';

const rotatingPlans = [
  { emoji: '🍝', label: 'dinners' },
  { emoji: '🚶', label: 'walks' },
  { emoji: '🎲', label: 'game nights' },
  { emoji: '🎨', label: 'creative plans' },
  { emoji: '☕', label: 'coffee hangs' },
  { emoji: '🌮', label: 'food crawls' },
  { emoji: '🏙️', label: 'city plans' },
  { emoji: '🍷', label: 'wine nights' },
  { emoji: '🌅', label: 'sunset hangs' },
  { emoji: '🧺', label: 'picnics' },
];

const HOW_IT_WORKS = [
  {
    icon: <LuCalendarCheck size={25} strokeWidth={2.1} />,
    title: 'Pick a gathering',
    text: 'Choose from dinners, walks, game nights, creative plans, and other small hosted experiences around LA.',
  },
  {
    icon: <LuUserRound size={25} strokeWidth={2.1} />,
    title: 'show up solo',
    text: 'Most people come by themselves, so you are not walking into someone else’s group.',
  },
  {
    icon: <LuHandshake size={25} strokeWidth={2.1} />,
    title: 'Meet through the host',
    text: 'Your host welcomes everyone, starts the group, and makes it easy for people to actually talk.',
  },
  {
    icon: <LuMessagesSquare size={25} strokeWidth={2.1} />,
    title: 'Leave with friends',
    text: 'Meet people during the gathering, then keep the connection going after through Tribe.',
  },
];

const FAQS = [
  {
    question: 'Do people actually come alone?',
    answer:
      'Yes. Most people come solo. That is the point. Tribe gatherings are designed so you are not walking into someone else’s friend group.',
  },
  {
    question: 'What happens at a gathering?',
    answer:
      'You show up, meet your host, and join a small group around a shared plan like dinner, a walk, games, coffee, or something creative. The host helps everyone get comfortable and keeps the group moving.',
  },
  {
    question: 'Is this an event app?',
    answer:
      'Not exactly. The gathering is just the starting point. Tribe is built to help you meet people in real life and turn those moments into actual friendships.',
  },
  {
    question: 'What if I am awkward or shy?',
    answer:
      'That is exactly who this is for. You do not have to perform or force conversation. The host, the activity, and the group structure make it easier to talk naturally.',
  },
  {
    question: 'Where are gatherings hosted?',
    answer:
      'Right now, gatherings are hosted around LA. We use restaurants, parks, cafes, bars, creative spaces, and other places that make sense for the plan.',
  },
  {
    question: 'Do I need to bring anyone?',
    answer:
      'No. You can come alone. You can also bring a friend, but the experience is built for solo people who want to meet new people.',
  },
];

export default function DesktopLanding() {
  const [portalLoginOpen, setPortalLoginOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const marqueeItems = [...rotatingPlans, ...rotatingPlans, ...rotatingPlans];

  return (
    <div style={styles.page} className="desktop-landing">
      <style>{`
        @keyframes tribePlanMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }

        .desktop-landing .tribe-black :is(path,circle,rect,polygon,polyline,ellipse) {
          fill: #050505 !important;
        }

        .desktop-landing .tribe-black [fill="none"] {
          fill: none !important;
        }

        .desktop-landing .tribe-black * {
          stroke: none !important;
        }

        .desktop-landing .tribe-white :is(path,circle,rect,polygon,polyline,ellipse) {
          fill: #000 !important;
        }

        .desktop-landing .tribe-white [fill="none"] {
          fill: none !important;
        }

        .desktop-landing .tribe-white * {
          stroke: none !important;
        }
      `}</style>

      <header style={styles.nav}>
        <button
          type="button"
          style={styles.logoButton}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
        >
          <TribeLogo className="tribe-black" style={styles.logoSvg} />
        </button>

        <button
          type="button"
          style={styles.portalLoginBtn}
          onClick={() => setPortalLoginOpen(true)}
        >
          <LuLogIn size={14} strokeWidth={2.4} />
          Portal login
        </button>
      </header>

      <main>
        <section style={styles.hero}>
          <div style={styles.heroText}>
            <p style={styles.eyebrow}>The friend-making app</p>

            <h1 style={styles.heroTitle}>
              show up solo.
              <br />
              Leave with friends.
            </h1>

            <p style={styles.heroSub}>
              We plan small hosted gatherings around LA so meeting new people feels
              natural, not forced.
            </p>

            <div style={styles.activityBlock} aria-label="Example gathering types">
              <p style={styles.activityLabel}>pick from plans like</p>

              <div style={styles.marqueeWindow}>
                <div style={styles.marqueeTrack}>
                  {marqueeItems.map((plan, index) => (
                    <span key={`${plan.label}-${index}`} style={styles.planItem}>
                      <span style={styles.planEmoji}>{plan.emoji}</span>
                      <span style={styles.planText}>{plan.label}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => {
                const section = document.getElementById('how-it-works');
                section?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              How it works
            </button>

            <div style={styles.qrSection}>
              <img src={JoinTribeQR} alt="Scan to join Tribe" style={styles.qrImage} />

              <div style={styles.qrCopy}>
                <div style={styles.qrTitle}>Scan to get started</div>
                <div style={styles.qrSub}>Open Tribe on your phone</div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" style={styles.howSection}>
          <div style={styles.howHeader}>
            <p style={styles.eyebrow}>How it works</p>
            <h2 style={styles.sectionTitle}>making friends should feel easier.</h2>
            <p style={styles.sectionSub}>
              Tribe turns real-life plans into simple, hosted experiences where
              people can actually connect.
            </p>
          </div>

          <div style={styles.stepsList}>
            {HOW_IT_WORKS.map((item, index) => (
              <div key={item.title} style={styles.stepRow}>
                <div style={styles.stepMeta}>
                  <span style={styles.stepNumber}>0{index + 1}</span>
                  <span style={styles.stepIcon}>{item.icon}</span>
                </div>

                <div style={styles.stepCopy}>
                  <h3 style={styles.stepTitle}>{item.title}</h3>
                  <p style={styles.stepText}>{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section style={styles.faqSection}>
          <div style={styles.faqHeader}>
            <p style={styles.eyebrow}>FAQ</p>
            <h2 style={styles.sectionTitle}>questions before you show up?</h2>
          </div>

          <div style={styles.faqList}>
            {FAQS.map((item, index) => {
              const isOpen = openFaq === index;

              return (
                <div key={item.question} style={styles.faqItem}>
                  <button
                    type="button"
                    style={styles.faqQuestion}
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                  >
                    <span>{item.question}</span>

                    <IoChevronDown
                      style={{
                        ...styles.faqIcon,
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                      }}
                    />
                  </button>

                  {isOpen && <p style={styles.faqAnswer}>{item.answer}</p>}
                </div>
              );
            })}
          </div>
        </section>
      </main>

      <footer style={styles.footer}>
        <div style={styles.footerTextContainer}>
          <p style={styles.footerLogoWrap}>
            <TribeLogo
              className="tribe-white"
              style={styles.footerLogoSvg}
              aria-label="Tribe"
            />
          </p>

          <p style={styles.footerCopy}>2024 TRIBE LLC. All Rights Reserved.</p>
        </div>

        <div style={styles.socialIconsContainer}>
          {[
            {
              href: 'https://www.instagram.com/jointribe.io/#',
              icon: <FaInstagram />,
            },
            {
              href: 'https://www.linkedin.com/company/82302259',
              icon: <FaLinkedin />,
            },
          ].map(({ href, icon }) => (
            <a
              key={href}
              href={href}
              target="_blank"
              rel="noreferrer"
              style={styles.iconLink}
            >
              {icon}
            </a>
          ))}
        </div>

        <div style={styles.footerLinksContainer}>
          <a href="/terms" style={styles.footerLink}>
            Terms of Service
          </a>

          <span style={styles.footerDot}>•</span>

          <a href="/privacy" style={styles.footerLink}>
            Privacy Policy
          </a>
        </div>
      </footer>

      <PortalLoginModal
        open={portalLoginOpen}
        onClose={() => setPortalLoginOpen(false)}
      />
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#fff',
    color: '#111',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    overflowX: 'hidden',
  },

  nav: {
    height: 76,
    maxWidth: 1180,
    margin: '0 auto',
    padding: '0 28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
  },

  logoButton: {
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },

  logoSvg: {
    width: 100,
    height: 'auto',
    display: 'block',
  },

  portalLoginBtn: {
    height: 40,
    padding: '0 14px',
    borderRadius: 999,
    border: '1px solid rgba(17,17,17,0.12)',
    background: '#fff',
    color: '#111',
    fontSize: 13,
    fontWeight: 760,
    letterSpacing: '-0.02em',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
  },

  hero: {
    maxWidth: 1180,
    margin: '0 auto',
    minHeight: 'calc(100vh - 76px)',
    padding: '72px 28px 94px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },

  heroText: {
    width: '100%',
    maxWidth: 900,
    minWidth: 0,
    textAlign: 'center',
  },

  eyebrow: {
    margin: 0,
    color: 'rgba(17,17,17,0.5)',
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },

  heroTitle: {
    margin: '16px auto 0',
    color: '#050505',
    fontSize: 'clamp(4rem, 5.8vw, 6.25rem)',
    lineHeight: 0.9,
    fontWeight: 880,
    letterSpacing: '-0.048em',
    maxWidth: 820,
  },

  heroSub: {
    margin: '26px auto 0',
    maxWidth: 620,
    color: 'rgba(17,17,17,0.62)',
    fontSize: '1.18rem',
    lineHeight: 1.38,
    fontWeight: 500,
    letterSpacing: '-0.025em',
  },

  activityBlock: {
    width: '100%',
    margin: '34px auto 0',
    overflow: 'hidden',
    position: 'relative',
    zIndex: 2,
  },

  activityLabel: {
    margin: '0 0 12px',
    fontSize: '0.74rem',
    lineHeight: 1,
    fontWeight: 760,
    letterSpacing: '0.11em',
    textTransform: 'uppercase',
    color: 'rgba(23,23,23,0.4)',
    textAlign: 'center',
  },

  marqueeWindow: {
    width: '100%',
    overflow: 'hidden',
    WebkitMaskImage:
      'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
    maskImage:
      'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
  },

  marqueeTrack: {
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
    width: 'max-content',
    animation: 'tribePlanMarquee 30s linear infinite',
    willChange: 'transform',
  },

  planItem: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
    whiteSpace: 'nowrap',
    fontSize: 'clamp(1.55rem, 2.1vw, 2.25rem)',
    lineHeight: 1.6,
    fontWeight: 700,
    letterSpacing: '-0.035em',
    color: '#171717',
    textTransform: 'lowercase',
  },

  planEmoji: {
    fontSize: '0.82em',
    lineHeight: 1.25,
    display: 'inline-flex',
    transform: 'translateY(-1px)',
  },

  planText: {
    display: 'inline-flex',
  },

  secondaryButton: {
    marginTop: 30,
    border: 'none',
    background: 'transparent',
    color: '#111',
    height: 38,
    padding: '0 2px',
    fontSize: '1rem',
    fontWeight: 780,
    letterSpacing: '-0.02em',
    cursor: 'pointer',
    borderBottom: '1px solid rgba(17,17,17,0.36)',
  },

  qrSection: {
    marginTop: 28,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  qrImage: {
    width: 164,
    height: 164,
    objectFit: 'contain',
    display: 'block',
  },

  qrCopy: {
    marginTop: 10,
    minWidth: 0,
    textAlign: 'center',
  },

  qrTitle: {
    color: '#171717',
    fontSize: '0.9rem',
    lineHeight: 1.1,
    fontWeight: 820,
    letterSpacing: '-0.025em',
  },

  qrSub: {
    marginTop: 4,
    color: 'rgba(23,23,23,0.52)',
    fontSize: '0.78rem',
    lineHeight: 1.2,
    fontWeight: 520,
    letterSpacing: '-0.018em',
  },

  howSection: {
    maxWidth: 1180,
    margin: '0 auto',
    padding: '90px 28px 96px',
    borderTop: '1px solid rgba(17,17,17,0.08)',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: '0.85fr 1.15fr',
    gap: 60,
  },

  howHeader: {
    maxWidth: 460,
  },

  sectionTitle: {
    margin: '12px 0 0',
    color: '#050505',
    fontSize: 'clamp(2rem, 4vw, 3rem)',
    lineHeight: 0.95,
    fontWeight: 830,
    letterSpacing: '-0.045em',
  },

  sectionSub: {
    margin: '18px 0 0',
    maxWidth: 650,
    color: 'rgba(17,17,17,0.58)',
    fontSize: '1.08rem',
    lineHeight: 1.42,
    fontWeight: 500,
    letterSpacing: '-0.02em',
  },

  stepsList: {
    borderTop: '1px solid rgba(17,17,17,0.1)',
  },

  stepRow: {
    display: 'grid',
    gridTemplateColumns: '105px minmax(0, 1fr)',
    gap: 0,
    padding: '24px 0',
    borderBottom: '1px solid rgba(17,17,17,0.1)',
    alignItems: 'start',
  },

  stepMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    color: 'rgba(17,17,17,0.48)',
  },

  stepNumber: {
    color: 'rgba(17,17,17,0.38)',
    fontSize: 13,
    lineHeight: 1,
    fontWeight: 850,
    letterSpacing: '0.08em',
  },

  stepIcon: {
    color: '#111',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  stepCopy: {
    maxWidth: 720,
  },

  stepTitle: {
    margin: 0,
    color: '#111',
    fontSize: '1.08rem',
    lineHeight: 1.08,
    fontWeight: 800,
    letterSpacing: '-0.03em',
  },

  stepText: {
    margin: '8px 0 0',
    color: 'rgba(17,17,17,0.58)',
    fontSize: '0.95rem',
    lineHeight: 1.45,
    fontWeight: 500,
    letterSpacing: '-0.016em',
  },

  faqSection: {
    maxWidth: 1180,
    margin: '0 auto',
    padding: '90px 28px 96px',
    borderTop: '1px solid rgba(17,17,17,0.08)',
    boxSizing: 'border-box',
    display: 'grid',
    gridTemplateColumns: '0.85fr 1.15fr',
    gap: 60,
  },

  faqHeader: {
    maxWidth: 460,
  },

  faqList: {
    borderTop: '1px solid rgba(17,17,17,0.1)',
  },

  faqItem: {
    borderBottom: '1px solid rgba(17,17,17,0.1)',
  },

  faqQuestion: {
    width: '100%',
    minHeight: 70,
    border: 'none',
    background: 'transparent',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
    color: '#111',
    fontSize: '1.04rem',
    fontWeight: 780,
    letterSpacing: '-0.028em',
    cursor: 'pointer',
  },

  faqIcon: {
    flexShrink: 0,
    fontSize: 18,
    transition: 'transform 160ms ease',
  },

  faqAnswer: {
    margin: '-4px 0 22px',
    color: 'rgba(17,17,17,0.58)',
    fontSize: '0.95rem',
    lineHeight: 1.48,
    fontWeight: 500,
    letterSpacing: '-0.016em',
  },

  footer: {
    backgroundColor: '#fff',
    padding: '32px 0 46px',
    borderTop: '1px solid rgba(17,17,17,0.08)',
  },

  footerTextContainer: {
    textAlign: 'center',
    margin: '0 auto',
    maxWidth: 420,
    padding: '0 20px',
  },

  footerLogoWrap: {
    margin: 0,
    marginBottom: 8,
    display: 'flex',
    justifyContent: 'center',
  },

  footerLogoSvg: {
    display: 'block',
    width: 78,
    height: 'auto',
    color: '#000000ff',
  },

  footerCopy: {
    marginTop: '0.65rem',
    fontSize: '0.74rem',
    color: '#000',
  },

  socialIconsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 20,
    marginTop: 18,
  },

  iconLink: {
    fontSize: '1.28rem',
    color: '#000',
    transition: 'color 0.2s ease',
  },

  footerLinksContainer: {
    marginTop: 12,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 9,
  },

  footerLink: {
    color: '#777',
    fontSize: '0.78rem',
    textDecoration: 'none',
  },

  footerDot: {
    color: '#999',
    fontSize: '0.78rem',
    userSelect: 'none',
  },
};